import { Check } from 'lucide-react';

import { CommandItem } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { EntityAvatar } from '@/modules/common';

import { OptionRendererProps, SingleSelectOption } from './single-select-types';

// Simple option renderer for non-searchable select
export function SelectItemContent({
  option,
  showAvatars,
  showDescriptionInOptions,
  avatarShape,
  avatarSize,
}: OptionRendererProps) {
  if (option.customContent) {
    return option.customContent;
  }

  return (
    <div className="flex w-full items-center">
      {showAvatars && (
        <EntityAvatar
          image={option.avatar}
          color={option.color}
          name={option.label}
          size={avatarSize ?? 'sm'}
          shape={avatarShape}
          className="mr-3 flex-shrink-0"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate">{option.label}</div>
        {showDescriptionInOptions && option.description && (
          <div className="truncate text-sm text-muted-foreground">{option.description}</div>
        )}
      </div>
    </div>
  );
}

// Command item wrapper for searchable select
interface CommandItemWrapperProps {
  option: SingleSelectOption;
  isSelected: boolean;
  onSelect: (value: string) => void;
  renderOption?: (option: SingleSelectOption, isSelected: boolean) => React.ReactNode;
  defaultRenderOption: (option: SingleSelectOption, isSelected: boolean) => React.ReactNode;
}

export function CommandItemWrapper({
  option,
  isSelected,
  onSelect,
  renderOption,
  defaultRenderOption,
}: CommandItemWrapperProps) {
  const handleSelect = () => {
    if (!option.disabled) {
      onSelect(option.value);
    }
  };

  return (
    <CommandItem
      value={option.value}
      onSelect={handleSelect}
      className={cn('cursor-pointer py-1', option.disabled && 'opacity-50 cursor-not-allowed')}
      disabled={option.disabled}
    >
      {option.customContent ||
        (renderOption ? renderOption(option, isSelected) : defaultRenderOption(option, isSelected))}
    </CommandItem>
  );
}

// Default option renderer for command-based select
export function createDefaultOptionRenderer({
  showAvatars,
  showDescriptionInOptions,
  avatarShape,
  avatarSize,
}: Omit<OptionRendererProps, 'option' | 'isSelected'>) {
  return (option: SingleSelectOption, isSelected: boolean) => (
    <div
      className={cn(
        'relative flex w-full cursor-default select-none items-center rounded-sm py-1 pr-8 pl-1 text-sm outline-none',
        'hover:bg-accent hover:text-accent-foreground',
        'focus:bg-accent focus:text-accent-foreground',
        isSelected && 'bg-accent text-accent-foreground'
      )}
    >
      <span className="absolute right-2 flex h-4 w-4 items-center justify-center">
        <Check
          className={cn(
            'h-4 w-4 transition-opacity',
            isSelected ? 'opacity-100 text-primary' : 'opacity-0'
          )}
        />
      </span>

      {showAvatars && (
        <EntityAvatar
          image={option.avatar}
          color={option.color}
          name={option.label}
          size={avatarSize ?? 'sm'}
          shape={avatarShape}
          className="mr-3 flex-shrink-0"
        />
      )}

      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'truncate font-normal leading-tight',
            isSelected ? 'text-accent-foreground' : 'text-foreground'
          )}
        >
          {option.label}
        </div>
        {showDescriptionInOptions && option.description && (
          <div
            className={cn(
              'text-xs text-muted-foreground truncate mt-0.5 leading-tight',
              isSelected && 'text-accent-foreground/70'
            )}
          >
            {option.description}
          </div>
        )}
      </div>
    </div>
  );
}
